import { motion } from "framer-motion";
import { Truck, PackageSearch, ShieldCheck, Clock, ArrowUpRight } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";

const SERVICES = [
  {
    icon: Truck,
    title: "Trasporti Nazionali",
    desc: "Trasporto merci su strada in tutta Italia con soluzioni personalizzate e affidabili.",
    num: "01",
  },
  {
    icon: PackageSearch,
    title: "Logistica Integrata",
    desc: "Gestione organizzata delle spedizioni e supporto logistico dedicato alle aziende.",
    num: "02",
  },
  {
    icon: ShieldCheck,
    title: "Trasporto Sicuro",
    desc: "Massima attenzione alla sicurezza e alla protezione della merce durante ogni tratta.",
    num: "03",
  },
  {
    icon: Clock,
    title: "Professionalità e Puntualità",
    desc: "Un servizio preciso, veloce e orientato alla piena soddisfazione del cliente.",
    num: "04",
  },
];

export default function Services() {
  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-[#070A12] overflow-hidden"
    >
      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 divider-glow" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[400px] w-[800px] bg-[#0055ff] opacity-[0.06] blur-[140px] rounded-full" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-blue-400" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-blue-300">
                I Nostri Servizi
              </span>
            </div>
            <h2
              data-testid="services-title"
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-[-0.02em] leading-[1.02] text-white max-w-3xl"
            >
              Soluzioni complete per{" "}
              <span className="metal-text-gradient">ogni esigenza</span> di
              trasporto.
            </h2>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta"
            className="hidden md:inline-flex items-center gap-2 px-6 py-4 border border-white/15 hover:border-blue-400 text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-blue-600 transition-all whitespace-nowrap self-start lg:self-end"
          >
            Parla con noi
            <ArrowUpRight size={16} />
          </a>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.08 * i }}
              className="group relative overflow-hidden border border-white/8 bg-gradient-to-b from-[#0A0D14] to-[#070912] hover:border-blue-500/50 transition-colors duration-500"
              data-testid={`service-card-${i}`}
            >
              {/* hover glow */}
              <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-blue-500/30 blur-[60px]" />
              </div>

              <div className="relative p-7 md:p-8 min-h-[320px] flex flex-col">
                <div className="flex items-start justify-between mb-8">
                  <div className="flex h-14 w-14 items-center justify-center bg-blue-600/10 border border-blue-500/30 group-hover:bg-blue-600/30 transition-colors">
                    <s.icon
                      size={26}
                      className="text-blue-400 group-hover:text-blue-300 transition-colors"
                      strokeWidth={2}
                    />
                  </div>
                  <span className="font-heading text-3xl font-black text-white/8 group-hover:text-blue-500/40 transition-colors">
                    {s.num}
                  </span>
                </div>

                <h3 className="font-heading text-xl md:text-2xl font-bold text-white leading-tight mb-3 uppercase tracking-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {s.desc}
                </p>

                <div className="mt-auto pt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-blue-300/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span>Scopri</span>
                  <ArrowUpRight size={14} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
